/**
 * BRSR Module - India SEBI Business Responsibility and Sustainability Reporting
 * 9 Principles covering Ethics, Product Sustainability, Employee Well-being, etc.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Save, RefreshCw, Download, FileText, Sparkles,
  CheckCircle, AlertCircle, TrendingUp
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const BRSRModule = ({ study }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [principles, setPrinciples] = useState([]);
  const [activePrinciple, setActivePrinciple] = useState(null);
  const [indicatorValues, setIndicatorValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [narratives, setNarratives] = useState({});
  const [reportingPeriod, setReportingPeriod] = useState('FY 2024-25');

  useEffect(() => {
    if (study?.id) {
      fetchData();
    }
  }, [study?.id]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/api/brsr/${study.id}/principles`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPrinciples(response.data.principles || []);

      // Initialize indicator values from saved data
      const values = {};
      response.data.principles?.forEach(principle => {
        principle.indicators?.forEach(indicator => {
          if (indicator.value !== undefined) {
            values[indicator.id] = {
              value: indicator.value,
              notes: indicator.notes || ''
            };
          }
        });
      });
      setIndicatorValues(values);

      // Set first principle as active
      if (response.data.principles?.length > 0) {
        setActivePrinciple(response.data.principles[0].id);
      }
    } catch (error) {
      console.error('Error fetching BRSR data:', error);
      showError('Failed to load BRSR data');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (indicatorId, field, value) => {
    setIndicatorValues(prev => ({
      ...prev,
      [indicatorId]: {
        ...prev[indicatorId],
        [field]: value
      }
    }));
  };

  const savePrincipleData = async (principleId) => {
    setSaving(true);
    const token = localStorage.getItem('token');

    // Collect all indicators across all principles
    const allIndicators = [];
    principles.forEach(principle => {
      principle.indicators.forEach(ind => {
        if (indicatorValues[ind.id]?.value) {
          allIndicators.push({
            principle_id: principle.id,
            indicator_name: ind.name,
            value: indicatorValues[ind.id]?.value || null,
            unit: ind.unit || "Value",
            remarks: indicatorValues[ind.id]?.notes || null
          });
        }
      });
    });

    try {
      await axios.post(
        `${API}/api/brsr/${study.id}/data`,
        { indicators: allIndicators },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Data saved successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
      showError('Failed to save data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const generateAIPoweredReport = async () => {
    setGeneratingNarrative(true);
    const token = localStorage.getItem('token');

    try {
      // First generate narratives for all principles
      showInfo('Generating AI-powered report with narratives... This may take a few minutes.');

      await axios.post(
        `${API}/api/brsr/${study.id}/generate-narrative`,
        { principle_id: "all" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Then download the PDF with narratives
      const response = await axios.get(
        `${API}/api/brsr/${study.id}/export/pdf?token=${token}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BRSR_Report_${study.name || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('AI-powered report generated and downloaded successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      showError(error.response?.data?.detail || 'Failed to generate AI-powered report');
    } finally {
      setGeneratingNarrative(false);
    }
  };

  const exportPDF = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${API}/api/brsr/${study.id}/export/pdf?reporting_period=${encodeURIComponent(reportingPeriod)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `brsr_report_${study.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showError('Failed to export PDF');
    }
  };

  const exportExcel = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${API}/api/brsr/${study.id}/export/excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `brsr_report_${study.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showError('Failed to export Excel');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-lg">Loading BRSR Module...</span>
      </div>
    );
  }

  const currentPrinciple = principles.find(p => p.id === activePrinciple);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg shadow-lg p-6 mb-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              BRSR - Business Responsibility & Sustainability Report
            </h1>
            <p className="text-blue-100">
              India SEBI BRSR Framework • {study?.study_name}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              Excel
            </button>
            <button
              onClick={exportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FileText className="w-4 h-4" />
              PDF Report
            </button>
            <button
              onClick={generateAIPoweredReport}
              disabled={generatingNarrative}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {generatingNarrative ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              AI-Powered Report
            </button>
          </div>
        </div>

        {/* Reporting Period */}
        <div className="mt-6 pt-4 border-t border-white/20">
          <label className="block text-sm font-semibold text-white mb-2">
            Reporting Period
          </label>
          <input
            type="text"
            value={reportingPeriod}
            onChange={(e) => setReportingPeriod(e.target.value)}
            className="px-4 py-2 border-2 border-white/30 bg-white/10 text-white placeholder-white/60 rounded-lg w-64 focus:outline-none focus:border-white focus:bg-white/20"
            placeholder="e.g., FY 2024-25"
          />
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar - Principles */}
        <div className="w-80 bg-white rounded-lg shadow-md p-4">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">9 Principles</h2>
          <div className="space-y-2">
            {principles.map((principle, index) => (
              <button
                key={principle.id}
                onClick={() => setActivePrinciple(principle.id)}
                className={`w-full text-left p-3 rounded-lg transition-all ${
                  activePrinciple === principle.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="font-medium text-sm">
                  P{index + 1}: {principle.title.replace(/^Principle \d+: /, '')}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content - Active Principle */}
        <div className="flex-1 bg-white rounded-lg shadow-md p-6">
          {currentPrinciple && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {currentPrinciple.title}
                </h2>
                <p className="text-gray-600">{currentPrinciple.description}</p>
              </div>

              {/* Indicators */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
                  Indicators
                </h3>

                {currentPrinciple.indicators.map((indicator) => (
                  <div key={indicator.id} className="bg-gray-50 rounded-lg p-4">
                    <label className="block font-medium text-gray-800 mb-2">
                      {indicator.name}
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      {indicator.description}
                    </p>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Value ({indicator.unit})
                        </label>
                        <input
                          type="text"
                          value={indicatorValues[indicator.id]?.value || ''}
                          onChange={(e) => handleValueChange(indicator.id, 'value', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder={`Enter ${indicator.unit}`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Notes
                        </label>
                        <input
                          type="text"
                          value={indicatorValues[indicator.id]?.notes || ''}
                          onChange={(e) => handleValueChange(indicator.id, 'notes', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Additional notes"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-3 border-t pt-6">
                <button
                  onClick={() => savePrincipleData(currentPrinciple.id)}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Data
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BRSRModule;
